// src/modules/quantum/HybridAI.jsx
// Hybrid AI — classical-quantum loops · PennyLane patterns · noise mitigation · VQE · architecture

import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Panel, Metric, LogPanel, timestamp } from '../../components/ui.jsx';

const AC = '#fb923c';
import { _livePalette as P } from '../../context/paletteSync';
const PI = Math.PI;

const Btn = ({onClick,children,color=AC,disabled,style}) => (
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'22',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1,...style}}>{children}</button>
);
const Sl = ({label,value,min,max,step=0.01,onChange,unit='',color=AC}) => (
  <div style={{marginBottom:'10px'}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
      <span style={{fontSize:'10px',color:'var(--t2)'}}>{label}</span>
      <span style={{fontSize:'10px',color,fontWeight:700}}>{typeof value==='number'?value.toFixed(step<0.01?4:step<1?2:0):value}{unit}</span>
    </div>
    <div style={{position:'relative',height:'3px',background:'var(--b0)',borderRadius:'2px'}}>
      <div style={{position:'absolute',left:0,height:'100%',background:`linear-gradient(90deg,${color}44,${color})`,width:`${((value-min)/(max-min))*100}%`}}/>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(parseFloat(e.target.value))}
        style={{position:'absolute',inset:'-7px 0',width:'100%',opacity:0,cursor:'pointer',height:'18px'}}/>
    </div>
  </div>
);

// ─── HYBRID LOOP SVG ─────────────────────────────────────────────────────────
function HybridLoopSVG({ phase }) {
  const W=680, H=220;
  const phases = [
    {label:'CLASSICAL\nPREPROCESS', sub:'normalize · encode · feature select', x:80,  color:'#22d3ee'},
    {label:'QUANTUM\nCIRCUIT',      sub:'parameterized gates · Ry/CNOT',       x:240, color:'#a78bfa'},
    {label:'QUANTUM\nMEASURE',      sub:'expectation ⟨Z⟩ · sample',            x:400, color:'#4ade80'},
    {label:'CLASSICAL\nOPTIMIZER', sub:'Adam/COBYLA · param-shift grad',       x:560, color:'#fb923c'},
  ];
  const arrows = [[0,1],[1,2],[2,3],[3,1]]; // last arrow loops back
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{background:'#060b14',borderRadius:'8px',border:'1px solid #1e293b',display:'block'}}>
      {/* Data flow label at top */}
      <text x={W/2} y={18} textAnchor="middle" style={{fontSize:'9px',fill:'#475569',fontFamily:'JetBrains Mono'}}>HYBRID QUANTUM-CLASSICAL VARIATIONAL LOOP</text>

      {/* Phase boxes */}
      {phases.map((p,i)=>{
        const active = phase===i;
        const lines = p.label.split('\n');
        return (
          <g key={i}>
            <rect x={p.x-60} y={50} width={120} height={90} rx={6}
              fill={active?p.color+'22':'#0f172a'} stroke={p.color} strokeWidth={active?2.5:1.5}
              style={{transition:'all .3s'}}/>
            {lines.map((l,j)=>(
              <text key={j} x={p.x} y={75+j*16} textAnchor="middle"
                style={{fontSize:'10px',fill:p.color,fontFamily:'Syne',fontWeight:800}}>{l}</text>
            ))}
            <text x={p.x} y={120} textAnchor="middle" style={{fontSize:'7px',fill:'#475569',fontFamily:'JetBrains Mono'}}>{p.sub.slice(0,24)}</text>
          </g>
        );
      })}

      {/* Forward arrows */}
      {[0,1,2].map(i=>{
        const from=phases[i], to=phases[i+1];
        const active = phase===i;
        return (
          <g key={i}>
            <line x1={from.x+60} y1={95} x2={to.x-60} y2={95}
              stroke={active?from.color:'#1e293b'} strokeWidth={active?2:1.5}
              style={{transition:'stroke .3s'}}/>
            <polygon points={`${to.x-62},90 ${to.x-50},95 ${to.x-62},100`}
              fill={active?from.color:'#1e293b'} style={{transition:'fill .3s'}}/>
            {active&&<circle r={4} fill={from.color}>
              <animateMotion dur="0.5s" repeatCount="1" path={`M${from.x+60},95 L${to.x-60},95`}/>
            </circle>}
          </g>
        );
      })}

      {/* Feedback loop arrow (optimizer → circuit) */}
      <path d={`M560,140 Q560,185 400,185 Q240,185 240,140`}
        fill="none" stroke={phase===3?'#fb923c':'#1e293b'} strokeWidth={phase===3?2:1.5}
        strokeDasharray={phase===3?'none':'5 3'} style={{transition:'stroke .3s'}}/>
      <polygon points="238,140 244,152 250,140" fill={phase===3?'#fb923c':'#1e293b'} style={{transition:'fill .3s'}}/>
      <text x={400} y={200} textAnchor="middle" style={{fontSize:'8px',fill:'#475569',fontFamily:'JetBrains Mono'}}>updated parameters θ → next iteration</text>

      {/* Phase indicator */}
      <text x={W/2} y={H-5} textAnchor="middle" style={{fontSize:'9px',fill:phases[phase]?.color||'#475569',fontFamily:'JetBrains Mono',fontWeight:700}}>
        {phase>=0?`Active: ${phases[phase]?.label.replace('\n',' ')}`:''}
      </text>
    </svg>
  );
}

// ─── VQE SIMULATION ──────────────────────────────────────────────────────────
// Simulate VQE finding H₂ ground state energy
// Actual ground state: −1.1366 Hartree (simplified model)
const VQE_TRUE_ENERGY = -1.1366;

const simVQE = (nLayers, lr, maxIter, noise) => {
  let theta = Array.from({length:nLayers*2}, ()=>(Math.random()-0.5)*PI);
  const history = [];

  const energy = (t) => {
    // Simplified H₂ energy landscape: E(θ) = E_true + offset * product of cosines
    let E = VQE_TRUE_ENERGY;
    for(let i=0;i<t.length;i++) E += 0.8*Math.cos(t[i])/(i+1);
    // Add noise
    E += (Math.random()-0.5)*noise*0.1;
    return E;
  };

  let bestE = Infinity;
  for(let it=0;it<maxIter;it++){
    const E = energy(theta);
    if(E < bestE) bestE = E;
    // Param-shift gradient
    const grad = theta.map((_,k)=>{
      const tp=[...theta]; tp[k]+=PI/2;
      const tm=[...theta]; tm[k]-=PI/2;
      return (energy(tp)-energy(tm))/2;
    });
    theta = theta.map((t,k)=>t - lr*grad[k]);
    if(it % Math.ceil(maxIter/40) === 0)
      history.push({iter:it, energy:+E.toFixed(5), best:+bestE.toFixed(5), true:VQE_TRUE_ENERGY, gap:+(E-VQE_TRUE_ENERGY).toFixed(5)});
  }
  return {history, finalE:bestE, theta};
};

// ─── NOISE MITIGATION ────────────────────────────────────────────────────────
const simNoisy = (errorRate, shots, mitigate) => {
  // Simulate measuring ⟨Z⟩ with depolarizing noise
  const ideal = 0.707; // cos(π/4)
  const noisy = ideal * Math.pow(1-errorRate, 4); // 4 gates
  if(!mitigate) return {ideal, noisy, mitigated:noisy, improvement:0};
  // Zero-noise extrapolation: run at 1x, 2x, 3x noise, extrapolate to 0
  const e1 = ideal*Math.pow(1-errorRate,4);
  const e2 = ideal*Math.pow(1-2*errorRate,4);
  const e3 = ideal*Math.pow(1-3*errorRate,4);
  // Richardson extrapolation: E_0 ≈ (3E_1 - 3E_2 + E_3) / 1
  const mitigated = Math.max(-1,Math.min(1, 3*e1 - 3*e2 + e3));
  return {ideal, noisy, mitigated, improvement:Math.abs(mitigated-ideal)/Math.abs(noisy-ideal)};
};

// ─── PENNYLANE CODE PATTERNS ─────────────────────────────────────────────────
const PENNYLANE_SNIPPETS = {
  vqc: {
    title: 'VQC Binary Classifier',
    code: `import pennylane as qml
import numpy as np

dev = qml.device("default.qubit", wires=2)

@qml.qnode(dev)
def circuit(x, params):
    # Encode data
    qml.RX(np.pi * x[0], wires=0)
    qml.RX(np.pi * x[1], wires=1)
    # Variational layers
    for l in range(len(params)):
        qml.RY(params[l][0], wires=0)
        qml.RY(params[l][1], wires=1)
        qml.CNOT(wires=[0,1])
    return qml.expval(qml.PauliZ(0))

# Train with param-shift gradient
opt = qml.AdamOptimizer(stepsize=0.1)
params = np.random.uniform(0, 2*np.pi, (3,2))
for epoch in range(100):
    params, loss = opt.step_and_cost(
        lambda p: mse_loss(circuit, X_train, y_train, p),
        params
    )`
  },
  vqe: {
    title: 'VQE Hydrogen Molecule',
    code: `import pennylane as qml
from pennylane import qchem

# Build H₂ Hamiltonian
symbols = ["H", "H"]
coordinates = np.array([0.0,0.0,-0.66,0.0,0.0,0.66])
H, n_qubits = qchem.molecular_hamiltonian(symbols, coordinates)

dev = qml.device("default.qubit", wires=n_qubits)

@qml.qnode(dev)
def ansatz(params):
    qml.BasisState([1,1,0,0], wires=range(n_qubits))
    qml.DoubleExcitation(params[0], wires=[0,1,2,3])
    return qml.expval(H)

# Optimize with gradient descent
opt = qml.GradientDescentOptimizer(stepsize=0.4)
theta = np.array(0.0, requires_grad=True)
for n in range(100):
    theta, energy = opt.step_and_cost(ansatz, theta)
print(f"Ground state: {energy:.5f} Ha")`
  },
  noise: {
    title: 'Zero-Noise Extrapolation',
    code: `import pennylane as qml
import mitiq  # quantum error mitigation

dev = qml.device("qiskit.ibmq", wires=2,
                  backend="ibmq_manila")  # real hardware

@qml.qnode(dev)
def noisy_circuit(params):
    qml.RY(params[0], wires=0)
    qml.CNOT(wires=[0,1])
    qml.RY(params[1], wires=1)
    return qml.expval(qml.PauliZ(0))

# Zero-noise extrapolation via Mitiq
from mitiq import zne

def executor(circuit):
    return noisy_circuit(params)

# Fold gates to amplify noise: 1x, 2x, 3x
mitigated_result = zne.execute_with_zne(
    circuit=circuit,
    executor=executor,
    factory=zne.RichardsonFactory([1.0, 2.0, 3.0])
)
print(f"Mitigated: {mitigated_result:.4f}")`
  },
  hybrid_nn: {
    title: 'Hybrid Quantum-Classical NN',
    code: `import pennylane as qml
import torch
import torch.nn as nn

# Quantum layer
dev = qml.device("default.qubit", wires=4)

@qml.qnode(dev, interface="torch")
def q_layer(inputs, weights):
    qml.AngleEmbedding(inputs, wires=range(4))
    qml.StronglyEntanglingLayers(weights, wires=range(4))
    return [qml.expval(qml.PauliZ(i)) for i in range(4)]

weight_shapes = {"weights": (3, 4, 3)}
qlayer = qml.qnn.TorchLayer(q_layer, weight_shapes)

# Hybrid model: classical → quantum → classical
model = nn.Sequential(
    nn.Linear(8, 4),      # Classical pre-processing
    nn.ReLU(),
    qlayer,               # Quantum layer
    nn.Linear(4, 2),      # Classical post-processing
    nn.Softmax(dim=1)
)

optimizer = torch.optim.Adam(model.parameters(), lr=0.01)
# Train normally with PyTorch — gradients flow through quantum layer`
  }
};

const TABS = ['loop','vqe','noise','pennylane','architectures'];

export default function HybridAI() {
  const [tab, setTab]   = useState('loop');
  const [logs, setLogs] = useState([{ts:'00:00:00',msg:'Hybrid AI — where quantum circuits meet classical ML in a single differentiable pipeline.',t:'info'}]);
  const log = (msg,t='info') => setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  // Loop animation
  const [loopPhase, setLoopPhase] = useState(-1);
  const [loopRunning, setLoopRunning] = useState(false);
  const loopRef = useRef();

  // VQE
  const [vqeLayers, setVqeLayers]   = useState(2);
  const [vqeLR, setVqeLR]           = useState(0.15);
  const [vqeIter, setVqeIter]       = useState(80);
  const [vqeNoise, setVqeNoise]     = useState(0.05);
  const [vqeResult, setVqeResult]   = useState(null);
  const [vqeRunning, setVqeRunning] = useState(false);

  // Noise
  const [errorRate, setErrorRate]   = useState(0.02);
  const [mitigate, setMitigate]     = useState(false);
  const noiseResult = simNoisy(errorRate, 1000, mitigate);

  // Code snippet
  const [snippet, setSnippet] = useState('vqc');

  const toggleLoop = () => {
    if(loopRunning){ clearInterval(loopRef.current); setLoopRunning(false); setLoopPhase(-1); return; }
    setLoopRunning(true); setLoopPhase(0);
    loopRef.current = setInterval(()=>setLoopPhase(p=>(p+1)%4), 900);
  };

  const runVQE = () => {
    setVqeRunning(true);
    setTimeout(()=>{
      const r = simVQE(vqeLayers, vqeLR, vqeIter, vqeNoise);
      setVqeResult(r);
      log(`VQE: ${vqeLayers} layers, noise=${vqeNoise} → E=${r.finalE.toFixed(5)} Ha (true=${VQE_TRUE_ENERGY})`,'ok');
      setVqeRunning(false);
    }, 80);
  };

  useEffect(()=>()=>clearInterval(loopRef.current),[]);

  // Overhead comparison data
  const overheadData = [
    {task:'2-qubit classify',classical:2,quantum:12,hybrid:6},
    {task:'10-qubit VQE',classical:1,quantum:8000,hybrid:40},
    {task:'MaxCut 20-node',classical:5,quantum:200,hybrid:15},
    {task:'Image classify',classical:10,quantum:5000,hybrid:25},
  ];

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?AC:'transparent'}`,background:tab===t?AC+'18':'transparent',color:tab===t?AC:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* ── LOOP ── */}
      {tab==='loop'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="HYBRID QUANTUM-CLASSICAL VARIATIONAL LOOP" accent={AC}>
            <HybridLoopSVG phase={loopPhase}/>
            <div style={{display:'flex',gap:'8px',marginTop:'10px'}}>
              <Btn onClick={toggleLoop} color={loopRunning?'#f87171':AC} style={{flex:1}}>{loopRunning?'STOP':'▶ ANIMATE LOOP'}</Btn>
              <Btn onClick={()=>{clearInterval(loopRef.current);setLoopRunning(false);setLoopPhase(-1);}} color="#64748b">RESET</Btn>
            </div>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
            <Panel title="WHAT THE CLASSICAL PART DOES" accent="#22d3ee">
              {[
                {task:'Data preprocessing',detail:'Normalize features, encode into rotation angles for quantum input'},
                {task:'Parameter optimization',detail:'Adam/COBYLA/SPSA adjust circuit params using quantum gradient estimates'},
                {task:'Loss computation',detail:'Converts quantum measurement outcomes to scalar loss'},
                {task:'Post-processing',detail:'Decodes quantum output (expectation values) into predictions'},
                {task:'Batching & caching',detail:'Manages mini-batches, caches intermediate results to reduce circuit calls'},
              ].map(({task,detail})=>(
                <div key={task} style={{display:'flex',gap:'8px',padding:'5px 8px',background:'var(--deep)',borderRadius:'2px',marginBottom:'3px',borderLeft:'2px solid #22d3ee'}}>
                  <span style={{fontSize:'9px',color:'#22d3ee',fontWeight:700,minWidth:140}}>{task}</span>
                  <span style={{fontSize:'9px',color:'var(--t3)'}}>{detail}</span>
                </div>
              ))}
            </Panel>
            <Panel title="WHAT THE QUANTUM PART DOES" accent="#a78bfa">
              {[
                {task:'Feature mapping',detail:'Encodes classical data into quantum states via rotation gates (Rx, Ry, Rz)'},
                {task:'Entanglement',detail:'CNOT/CZ gates create correlations impossible to simulate cheaply classically'},
                {task:'Parameterized evolution',detail:'Variational gates explore exponentially large Hilbert space'},
                {task:'Expectation measurement',detail:'⟨Z⟩, ⟨X⟩, ⟨ZZ⟩ — returns real-valued gradient-accessible outputs'},
                {task:'Quantum advantage region',detail:'Circuits too deep to simulate classically — potential speedup territory'},
              ].map(({task,detail})=>(
                <div key={task} style={{display:'flex',gap:'8px',padding:'5px 8px',background:'var(--deep)',borderRadius:'2px',marginBottom:'3px',borderLeft:'2px solid #a78bfa'}}>
                  <span style={{fontSize:'9px',color:'#a78bfa',fontWeight:700,minWidth:140}}>{task}</span>
                  <span style={{fontSize:'9px',color:'var(--t3)'}}>{detail}</span>
                </div>
              ))}
            </Panel>
          </div>
          <LogPanel logs={logs} height={70}/>
        </div>
      )}

      {/* ── VQE ── */}
      {tab==='vqe'&&(
        <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="VQE CONFIG" accent="#4ade80">
              <Sl label="Ansatz layers" value={vqeLayers} min={1} max={5} step={1} onChange={setVqeLayers} color="#4ade80"/>
              <Sl label="Learning rate" value={vqeLR} min={0.01} max={0.5} step={0.01} onChange={setVqeLR} color={AC}/>
              <Sl label="Iterations" value={vqeIter} min={20} max={200} step={10} onChange={setVqeIter} color="#22d3ee"/>
              <Sl label="Noise level" value={vqeNoise} min={0} max={0.5} step={0.01} onChange={setVqeNoise} color="#f87171"/>
              <Btn onClick={runVQE} disabled={vqeRunning} color="#4ade80" style={{width:'100%'}}>{vqeRunning?'RUNNING…':'RUN VQE →'}</Btn>
              {vqeResult&&<div style={{marginTop:'8px'}}>
                <Metric label="Final energy" value={`${vqeResult.finalE.toFixed(5)} Ha`} color="#4ade80"/>
                <Metric label="True ground" value={`${VQE_TRUE_ENERGY} Ha`} color="#22d3ee"/>
                <Metric label="Gap" value={`${(vqeResult.finalE-VQE_TRUE_ENERGY).toFixed(5)}`} color={Math.abs(vqeResult.finalE-VQE_TRUE_ENERGY)<0.01?'#4ade80':'#fbbf24'}/>
              </div>}
            </Panel>
            <Panel title="VQE EXPLAINED" accent={AC}>
              <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.8}}>
                VQE finds ground state energy of a quantum system (e.g., H₂) that's classically intractable at scale.
                <div style={{marginTop:'6px',padding:'8px',background:'var(--deep)',borderRadius:'3px',fontFamily:'JetBrains Mono',fontSize:'9px',color:'#4ade80',lineHeight:1.8}}>
                  E₀ ≤ ⟨ψ(θ)|H|ψ(θ)⟩<br/>
                  Variational principle:<br/>
                  any |ψ⟩ gives upper bound
                </div>
              </div>
            </Panel>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {vqeResult?(
              <Panel title={`VQE CONVERGENCE — H₂ GROUND STATE (true = ${VQE_TRUE_ENERGY} Ha)`} accent="#4ade80">
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={vqeResult.history}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="iter" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'iteration',fill:'var(--t3)',fontSize:8,position:'insideBottom',offset:-2}}/>
                    <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                    <Tooltip/><Legend/>
                    <Line type="monotone" dataKey="energy" stroke={AC}       strokeWidth={1.5} dot={false} name="VQE energy"/>
                    <Line type="monotone" dataKey="best"   stroke="#4ade80"  strokeWidth={2}   dot={false} name="Best so far"/>
                    <Line type="monotone" dataKey="true"   stroke="#64748b"  strokeWidth={1}   dot={false} strokeDasharray="5 3" name="True ground state"/>
                  </LineChart>
                </ResponsiveContainer>
              </Panel>
            ):<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:220,color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Configure and run VQE</div>}
            <Panel title="VQE APPLICATIONS" accent="#22d3ee">
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'8px'}}>
                {[
                  {app:'Drug discovery',desc:'Find ground state of drug molecules. Classical intractable at 50+ atoms.',color:'#4ade80'},
                  {app:'Materials science',desc:'Battery electrolytes, superconductors, catalysts — quantum chemistry.',color:'#22d3ee'},
                  {app:'Fertilizer efficiency',desc:'N₂ fixation (Haber-Bosch). Quantum VQE could reduce 2% global energy use.',color:'#a78bfa'},
                ].map(({app,desc,color})=>(
                  <div key={app} style={{padding:'10px',background:'var(--deep)',borderRadius:'3px',borderTop:`2px solid ${color}`}}>
                    <div style={{fontSize:'10px',color,fontWeight:700,marginBottom:'4px'}}>{app}</div>
                    <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.5}}>{desc}</div>
                  </div>
                ))}
              </div>
            </Panel>
          </div>
        </div>
      )}

      {/* ── NOISE ── */}
      {tab==='noise'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'240px 1fr',gap:'12px'}}>
            <Panel title="NOISE CONFIG" accent="#f87171">
              <Sl label="Gate error rate" value={errorRate} min={0.001} max={0.1} step={0.001} onChange={setErrorRate} color="#f87171"/>
              <div style={{display:'flex',gap:'6px',marginBottom:'10px'}}>
                {[['off','No mitigation'],['on','ZNE mitigation']].map(([v,l])=>(
                  <button key={v} onClick={()=>setMitigate(v==='on')} style={{flex:1,padding:'8px',border:`1px solid ${(v==='on')===mitigate?'#4ade80':'var(--b0)'}`,background:(v==='on')===mitigate?'#4ade8018':'var(--deep)',color:(v==='on')===mitigate?'#4ade80':'var(--t3)',fontSize:'9px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:(v==='on')===mitigate?700:400}}>{l}</button>
                ))}
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr',gap:'6px'}}>
                <Metric label="Ideal value" value={noiseResult.ideal.toFixed(4)} color="#4ade80"/>
                <Metric label="Noisy value" value={noiseResult.noisy.toFixed(4)} color="#f87171"/>
                <Metric label="Mitigated" value={noiseResult.mitigated.toFixed(4)} color={mitigate?"#fbbf24":"#64748b"}/>
                <Metric label="Error remaining" value={`${(Math.abs(noiseResult.mitigated-noiseResult.ideal)*100).toFixed(2)}%`} color={mitigate?"#fbbf24":"#f87171"}/>
              </div>
            </Panel>
            <Panel title="NOISE IMPACT vs ERROR RATE — WITH & WITHOUT MITIGATION" accent="#f87171">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={Array.from({length:30},(_,i)=>{
                  const er=0.001+i*0.003;
                  const n=simNoisy(er,1000,false);
                  const m=simNoisy(er,1000,true);
                  return {er:+er.toFixed(3),noisy:+n.noisy.toFixed(4),mitigated:+m.mitigated.toFixed(4),ideal:n.ideal};
                })}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                  <XAxis dataKey="er" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'gate error rate',fill:'var(--t3)',fontSize:8,position:'insideBottom',offset:-2}}/>
                  <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                  <Tooltip/><Legend/>
                  <Line type="monotone" dataKey="ideal"     stroke="#4ade80" strokeWidth={1.5} dot={false} strokeDasharray="5 3" name="Ideal"/>
                  <Line type="monotone" dataKey="noisy"     stroke="#f87171" strokeWidth={2}   dot={false} name="Noisy"/>
                  <Line type="monotone" dataKey="mitigated" stroke="#fbbf24" strokeWidth={2}   dot={false} name="ZNE mitigated"/>
                </LineChart>
              </ResponsiveContainer>
            </Panel>
          </div>
          <Panel title="NOISE MITIGATION TECHNIQUES" accent="#fbbf24">
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px'}}>
              {[
                {name:'Zero-Noise Extrapolation (ZNE)',desc:'Run at 1×, 2×, 3× noise. Fit curve. Extrapolate to zero noise. Works on any circuit. Implemented in Mitiq.',cost:'+2-3× circuit runs',color:'#fbbf24'},
                {name:'Probabilistic Error Cancellation',desc:'Characterize noise as quasi-probability distribution. Sample corrective operations. Exact but overhead grows exponentially.',cost:'+exponential overhead',color:'#22d3ee'},
                {name:'Symmetry Verification',desc:'Post-select on results that respect known symmetries of the problem (e.g., particle number conservation). Cheap.',cost:'Reduces sample efficiency',color:'#4ade80'},
                {name:'Dynamical Decoupling',desc:'Insert refocusing pulses between gates to cancel low-frequency noise. Works at pulse level. IBM/Qiskit native.',cost:'Near-zero overhead',color:'#a78bfa'},
                {name:'Readout Error Mitigation',desc:'Measure all bit strings. Build confusion matrix. Invert to correct measurement bias. Cheap, highly effective.',cost:'+2ⁿ calibration circuits',color:'#fb923c'},
                {name:'Clifford Data Regression',desc:'Run random Clifford circuits (classically simulable). Learn noise model. Apply correction to target circuit.',cost:'+training overhead',color:'#f87171'},
              ].map(({name,desc,cost,color})=>(
                <div key={name} style={{padding:'10px',background:'var(--deep)',borderRadius:'3px',borderTop:`2px solid ${color}`}}>
                  <div style={{fontSize:'9px',color,fontWeight:700,marginBottom:'5px'}}>{name}</div>
                  <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.6,marginBottom:'5px'}}>{desc}</div>
                  <div style={{fontSize:'8px',color:'#64748b',fontFamily:'JetBrains Mono'}}>Cost: {cost}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── PENNYLANE ── */}
      {tab==='pennylane'&&(
        <div style={{display:'grid',gridTemplateColumns:'180px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
            <div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',padding:'0 2px',marginBottom:'2px'}}>SNIPPET</div>
            {Object.entries(PENNYLANE_SNIPPETS).map(([k,v])=>(
              <button key={k} onClick={()=>setSnippet(k)} style={{padding:'8px',background:snippet===k?AC+'18':'var(--deep)',border:`1px solid ${snippet===k?AC:'var(--b0)'}`,color:snippet===k?AC:'var(--t3)',fontSize:'9px',cursor:'pointer',borderRadius:'3px',textAlign:'left',fontFamily:'JetBrains Mono',fontWeight:snippet===k?700:400}}>
                {v.title}
              </button>
            ))}
          </div>
          <Panel title={PENNYLANE_SNIPPETS[snippet].title} accent={AC}>
            <pre style={{background:'#060b14',border:'1px solid #1e293b',borderRadius:'4px',padding:'14px',fontSize:'9px',color:'#94a3b8',fontFamily:'JetBrains Mono',overflow:'auto',maxHeight:380,margin:0,lineHeight:1.7,whiteSpace:'pre-wrap'}}>
              {PENNYLANE_SNIPPETS[snippet].code}
            </pre>
          </Panel>
        </div>
      )}

      {/* ── ARCHITECTURES ── */}
      {tab==='architectures'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
            {[
              {name:'Serial Hybrid',color:'#22d3ee',diagram:'Classical → Quantum → Classical',desc:'Output of classical net feeds quantum circuit. Quantum output feeds next classical layer. Simplest architecture.',pro:'Easy to implement, works in PyTorch/TF',con:'Bottleneck at quantum-classical interface'},
              {name:'Parallel Hybrid',color:'#4ade80',diagram:'Classical ─┐\n             ├→ Merge → Output\nQuantum  ─┘',desc:'Classical and quantum paths run in parallel. Merge outputs by concatenation or weighted sum.',pro:'Classical handles easy features, quantum handles hard',con:'Coordination overhead, harder to train'},
              {name:'Quantum Layer (QNN)',color:'#a78bfa',diagram:'Dense → QLayer → Dense → Softmax',desc:'Quantum circuit acts as a single differentiable layer inside a classical neural net. PennyLane TorchLayer.',pro:'Drop-in replacement, full autodiff support',con:'Barren plateaus, limited depth'},
              {name:'VQE/QAOA + Classical',color:'#fbbf24',diagram:'Problem → QAOA/VQE → Solution → Classical post',desc:'Quantum solver for hard subproblem. Classical handles preprocessing and constraint satisfaction.',pro:'Uses quantum where it helps most',con:'Interface complexity, latency'},
            ].map(({name,color,diagram,desc,pro,con})=>(
              <Panel key={name} title={name} accent={color}>
                <pre style={{fontFamily:'JetBrains Mono',fontSize:'9px',color:color,background:'var(--deep)',padding:'8px',borderRadius:'3px',marginBottom:'8px',whiteSpace:'pre'}}>{diagram}</pre>
                <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.6,marginBottom:'5px'}}>{desc}</div>
                <div style={{fontSize:'9px',marginBottom:'2px'}}><span style={{color:'#4ade80'}}>✓ </span><span style={{color:'var(--t3)'}}>{pro}</span></div>
                <div style={{fontSize:'9px'}}><span style={{color:'#f87171'}}>✗ </span><span style={{color:'var(--t3)'}}>{con}</span></div>
              </Panel>
            ))}
          </div>
          <Panel title="CIRCUIT DEPTH vs CLASSICAL SIMULATION THRESHOLD" accent="#f87171">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
              <div>
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={Array.from({length:50},(_,i)=>({depth:i+1,classicalCost:Math.min(1e6,Math.pow(2,Math.min(40,(i+1)*0.8))),quantumCost:Math.pow(i+1,2)*10}))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="depth" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'circuit depth',fill:'var(--t3)',fontSize:8,position:'insideBottom',offset:-2}}/>
                    <YAxis tick={{fontSize:9,fill:'var(--t3)'}} scale="log" domain={['auto','auto']}/>
                    <Tooltip/><Legend/>
                    <Line type="monotone" dataKey="classicalCost" stroke="#f87171" strokeWidth={2} dot={false} name="Classical sim cost"/>
                    <Line type="monotone" dataKey="quantumCost"   stroke="#4ade80" strokeWidth={2} dot={false} name="Quantum run cost"/>
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.8}}>
                <div style={{color:'#f87171',fontWeight:700,marginBottom:'6px'}}>Classical simulation threshold</div>
                Shallow circuits (&lt;~40 gates): simulable classically. No quantum advantage.
                <br/><br/>
                Deep circuits: classical simulation exponentially expensive → quantum computer required.
                <br/><br/>
                <div style={{color:'#4ade80',fontWeight:700,marginBottom:'4px'}}>Hybrid sweet spot</div>
                Use quantum only for circuits beyond classical simulation. Keep everything else classical.
                <div style={{marginTop:'8px',padding:'6px 8px',background:'var(--deep)',borderRadius:'3px',borderLeft:'2px solid #fbbf24',fontFamily:'JetBrains Mono',fontSize:'8px',color:'#fbbf24'}}>
                  Rule of thumb: &gt;50 qubits × &gt;50 gates → classical simulation expensive
                </div>
              </div>
            </div>
          </Panel>
          <LogPanel logs={logs} height={70}/>
        </div>
      )}
    </div>
  );
}
